<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import ProfilePictureInput from "@/components/ui/ProfilePictureInput.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import TextInput from "@/components/Inputs/TextInput.vue";
import DateInput from "@/components/Inputs/DateInput.vue";
import InformationText from "@/components/InformationText.vue";

type Props = {
  name: string;
  birthday: string;
  pictureUrl?: string;
  updating?: boolean;
  nameValid?: boolean;
  birthdayValid?: boolean;
  profileValid?: boolean;
};

type Emits = {
  (e: "update:name", value: string): void;
  (e: "update:birthday", value: string): void;
  (e: "picture-change", file: File): void;
  (e: "save"): void;
};

const props = withDefaults(defineProps<Props>(), {
  pictureUrl: "",
  updating: false,
  nameValid: true,
  birthdayValid: true,
  profileValid: true,
});

const emit = defineEmits<Emits>();
const { t } = useI18n();

const localName = computed({
  get: () => props.name,
  set: (value: string) => emit("update:name", value),
});

const localBirthday = computed({
  get: () => props.birthday,
  set: (value: string) => emit("update:birthday", value),
});

function handlePictureChange(file: File): void {
  emit("picture-change", file);
}

function handleSave(): void {
  emit("save");
}
</script>

<template>
  <div class="mb-8">
    <h2
      class="mb-3 flex items-center gap-2 text-xl font-bold text-gray-900 dark:text-slate-200"
    >
      <span class="text-2xl">⭐</span>
      {{ t("settings.default_profile") }}
    </h2>

    <information-text
      :information="t('settings.default_profile_settings_information')"
      class="mb-4"
    />

    <div
      class="rounded-lg border-2 border-black-dark bg-white p-4 shadow-yellow-hard-sm dark:bg-kekkle-dark"
    >
      <!-- Profile Picture and Name - Side by side -->
      <div class="mb-4 flex gap-4">
        <profile-picture-input
          :current-image-url="pictureUrl"
          :label="t('settings.profile_picture')"
          @change="handlePictureChange"
        />

        <!-- Name Input - Takes remaining space -->
        <div class="flex-1">
          <text-input
            v-model="localName"
            :placeholder="t('global.inputs.placeholders.your_name')"
            :label="t('global.inputs.labels.name') + '*'"
          />
          <p v-show="!nameValid" class="mt-1 text-sm text-red-600">
            {{ t("global.inputs.errors.name") }}
          </p>
        </div>
      </div>

      <!-- Birthday -->
      <div class="mb-4">
        <h3 class="mb-1 font-medium dark:text-slate-200">
          {{ t("global.inputs.labels.birthday") }}
        </h3>
        <date-input v-model="localBirthday" />
        <p v-show="!birthdayValid" class="mt-1 text-sm text-red-600">
          {{ t("global.inputs.errors.date") }}
        </p>
      </div>

      <!-- Save Button -->
      <default-button
        color="yellow"
        :loading="updating"
        :disabled="!profileValid"
        full-width
        @click="handleSave"
      >
        <span v-if="updating">
          {{ t("global.inputs.labels.saving") }}
        </span>
        <span v-else>
          {{ t("global.inputs.labels.save") }}
        </span>
      </default-button>
    </div>
  </div>
</template>
